// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 82e92ef4-64df-4f42-af86-99812ffc8d0c

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Alert, CircularProgress, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, Divider, Tooltip,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { getConfig } from './config';
import { getIdToken, getSession } from './auth';
import store from './store';
import { exportCsv } from './exports';
import HelpBubble from './HelpBubble';

// FinOps — the read side of the cloud-connection feature. One GET
// (/finops/overview, backend/lambda/finops.js) returns everything on this
// screen. Who sees what is decided by the BACKEND, not here: a client user
// always gets their own tenant's connection; a master viewing a client
// workspace passes that tenantId; a master sitting in 'optimum' passes no
// tenantId and gets the Optimum org pilot (Optimum's own payer account via
// the SSM-published role). Every payload is single-tenant by construction —
// exactly one connection answered it — so grouping accounts/services below
// cannot blend clients the way store.listFactSheets('optimum', …) can.
//
// The payload never throws and never blanks: {connected:false, reason,
// detail} at the top, and each SECTION (spendSummary, spendByService,
// anomalies, each recommendation source) degrades independently to
// {available:false, reason}. This screen mirrors that honesty — a failed
// section shows its own reason, never an empty box, and every on-screen cap
// says what it dropped.

const HEAD = 'sectionHeader.main';

// On-screen cap for the per-account spend table. The full account list still
// backs the totals; the cap only limits ROWS, and the strip under the table
// announces exactly how many accounts (and how much spend) it left out.
const ACCOUNT_CAP = 10;

const fmtMoney = (v, ccy) => {
  if (v === undefined || v === null || v === '' || !Number.isFinite(Number(v))) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: ccy || 'USD' }).format(Number(v));
  } catch (e) {
    return `${v} ${ccy || ''}`;
  }
};

const fmtPct = (v) => (Number.isFinite(Number(v)) ? `${Number(v).toFixed(1)}%` : '—');

// Cost Explorer period starts are bare YYYY-MM-DD strings — parse as UTC so
// the label never slips a month for viewers west of Greenwich.
const fmtMonth = (iso) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const fmtDay = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
};

// A horizontal bar built from two Boxes — track on chipMuted, fill on
// primary — so it recolors itself with the theme in both modes. No SVG, no
// chart dependency.
function Bar({ value, max }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <Box sx={{ bgcolor: 'chipMuted.main', borderRadius: 1, height: 10, width: '100%', overflow: 'hidden' }}>
      <Box sx={{ bgcolor: 'primary.main', height: '100%', width: `${pct}%`, borderRadius: 1 }} />
    </Box>
  );
}

// Every degradable section renders this when its payload says
// {available:false, reason} — the backend's real reason, never a blank box.
// `cloud` defaults to AWS; the Azure pilot section below passes its own.
function SectionUnavailable({ what, reason, cloud = 'AWS' }) {
  return (
    <Alert severity="warning" sx={{ mt: 1 }}>
      {what} could not be read from {cloud} this time: {reason || 'no reason reported'}
    </Alert>
  );
}

export default function FinOps({ tenantId }) {
  const me = getSession();
  const iAmMaster = !!me && (me.groups || []).includes('OptimumAdmins');
  const iAmAdmin = iAmMaster || (me && me.roleId === 'admin');
  const isOrgPilot = tenantId === 'optimum';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // Azure state — the org pilot for a master in 'optimum', or the
  // workspace's own Lighthouse-delegated read in a client workspace (see the
  // render guard below). Its own state so the AWS and Azure halves load and
  // fail independently. azureConn is the workspace's 'azure' connection row
  // from /finops/connections (loaded alongside showback) — its presence is
  // what decides between a degraded strip and no Azure section at all.
  const [azure, setAzure] = useState(null);
  const [azureLoading, setAzureLoading] = useState(false);
  const [azureErr, setAzureErr] = useState('');
  const [azureConn, setAzureConn] = useState(null);
  // Azure Advisor. Loaded separately from the cost overview because it is a
  // different question -- what could be SAVED rather than what was SPENT -- and
  // because a slow or refused Advisor call must not hold up the spend figures,
  // which are the ones people come to this screen for.
  const [azureInsights, setAzureInsights] = useState(null);

  // Showback state — client workspaces only (the master/'optimum' workspace
  // is refused by the backend: showback is per client by definition). Its
  // own state so it loads and fails independently of the overview above.
  const [showback, setShowback] = useState(null);
  const [allocations, setAllocations] = useState([]);
  // Refused recommendations. A savings headline nobody can hit gets ignored,
  // so what cannot be done comes out of the total and is reported separately
  // rather than silently dropped -- "did you look at Graviton" needs an answer
  // that survives the person who looked at it.
  const [dismissals, setDismissals] = useState([]);
  const [dismissTarget, setDismissTarget] = useState(null);   // the rec being refused
  const [dismissReason, setDismissReason] = useState('');
  const [dismissBusy, setDismissBusy] = useState(false);
  const [apps, setApps] = useState([]);
  const [sbLoading, setSbLoading] = useState(false);
  const [sbErr, setSbErr] = useState('');
  // Showback V2 — the connection row (for its appTagKey and the payer account
  // the tag-key save must echo back) and the admin's tag-key draft.
  const [awsConn, setAwsConn] = useState(null);
  const [tagKeyDraft, setTagKeyDraft] = useState('');
  const [tagBusy, setTagBusy] = useState(false);
  const [allocOpen, setAllocOpen] = useState(false);
  const [allocBusy, setAllocBusy] = useState(false);
  const [allocErr, setAllocErr] = useState('');
  // accountId -> { factSheetId, pct, note } — the add-a-rule draft per
  // account inside the Allocate dialog.
  const [draft, setDraft] = useState({});

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    // Same "an error envelope is often HTML, not JSON" guard as every other
    // screen's api() — a raw JSON.parse used to throw before the real status
    // could ever be reported.
    let body = {};
    let parseFailed = false;
    try { body = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
    if (!res.ok) throw new Error(body.error || body.message || `request failed (${res.status})`);
    if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
    return body;
  }, []);

  const confirmDismiss = useCallback(async () => {
    if (!dismissTarget || !dismissReason.trim()) return;
    setDismissBusy(true);
    try {
      await api('/finops/dismissals', {
        method: 'POST',
        body: JSON.stringify({ tenantId, ...dismissTarget, reason: dismissReason.trim() }),
      });
      setDismissTarget(null); setDismissReason('');
      // Reload the OVERVIEW too, not just the list: the whole point is that the
      // savings total recalculates, and a stale headline beside a fresh
      // dismissal list is the confusing half-state.
      await Promise.all([load(), loadShowback()]);
    } catch (e) { setErr(e.message); } finally { setDismissBusy(false); }
  }, [api, dismissTarget, dismissReason, tenantId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const restoreDismissal = useCallback(async (id) => {
    setDismissBusy(true);
    try {
      await api(`/finops/dismissals?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
      await Promise.all([load(), loadShowback()]);
    } catch (e) { setErr(e.message); } finally { setDismissBusy(false); }
  }, [api]);   // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      // 'optimum' (master workspace) sends NO tenantId → the backend's org
      // pilot path. Any other workspace names itself; the backend ignores the
      // parameter for non-master callers and scopes to their own tenant.
      const qs = tenantId && tenantId !== 'optimum' ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
      setData(await api(`/finops/overview${qs}`));
    } catch (e) {
      setErr(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api, tenantId]);

  // Azure — one GET (/finops/azure/overview). The master 'optimum'
  // workspace sends NO tenantId → the backend's org pilot (master-only on
  // that branch); any other workspace names itself and reads its own
  // Lighthouse delegation — the backend ignores the parameter for
  // non-master callers and scopes to their own tenant, same as the AWS
  // overview. A not-connected Azure ({connected:false, reason}) arrives
  // inside a 200, so azureErr only ever carries a genuinely failed request.
  const loadAzure = useCallback(async () => {
    if (tenantId === 'optimum' && !iAmMaster) return;
    setAzureLoading(true); setAzureErr('');
    try {
      const qs = tenantId && tenantId !== 'optimum' ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
      setAzure(await api(`/finops/azure/overview${qs}`));
      try {
        if (tenantId && tenantId !== 'optimum') {
          setAzureInsights(await api(`/finops/azure/insights?tenantId=${encodeURIComponent(tenantId)}&want=advisor`));
        } else { setAzureInsights(null); }
      } catch (e) { setAzureInsights(null); }   // advisor is additive, never fatal
    } catch (e) {
      setAzureErr(e.message);
      setAzure(null);
    } finally {
      setAzureLoading(false);
    }
  }, [api, tenantId, iAmMaster]);

  // Showback + allocation rules + the Application list for the picker, one
  // load. store.listFactSheets is called with THIS workspace's tenantId and
  // only id/name are read — nothing here groups across tenants.
  const loadShowback = useCallback(async () => {
    if (!tenantId || tenantId === 'optimum') { setShowback(null); return; }
    setSbLoading(true); setSbErr('');
    try {
      const [sb, alloc, appList, conns] = await Promise.all([
        api(`/finops/showback?tenantId=${encodeURIComponent(tenantId)}&months=3`),
        api(`/finops/allocations?tenantId=${encodeURIComponent(tenantId)}`),
        store.listFactSheets(tenantId, { type: 'Application' }),
        api(`/finops/connections?tenantId=${encodeURIComponent(tenantId)}`),
      ]);
      setShowback(sb);
      setAllocations(Array.isArray(alloc) ? alloc : []);
      try {
        const d = await api(`/finops/dismissals?tenantId=${encodeURIComponent(tenantId)}`);
        setDismissals(Array.isArray(d) ? d : []);
      } catch (e) { setDismissals([]); }   // a missing list must not break the screen
      setApps(appList || []);
      const aws = (Array.isArray(conns) ? conns : []).find((c) => c.provider === 'aws') || null;
      setAwsConn(aws);
      setTagKeyDraft((aws && aws.appTagKey) || '');
      // The 'azure' row from the same listing gates the Azure section below:
      // present + read failed = honest degraded strip; absent = no Azure
      // section at all, so an AWS-only workspace sees no Azure noise.
      setAzureConn((Array.isArray(conns) ? conns : []).find((c) => c.provider === 'azure') || null);
    } catch (e) {
      setSbErr(e.message);
      setShowback(null);
    } finally {
      setSbLoading(false);
    }
  }, [api, tenantId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAzure(); }, [loadAzure]);
  useEffect(() => { loadShowback(); }, [loadShowback]);

  // Aggregate per-account spend across the returned months. Safe to key by
  // accountId alone: the whole payload came through ONE tenant's connection
  // (or the Optimum pilot), so there is no cross-tenant merge to guard here.
  const accountTotals = useMemo(() => {
    const s = data && data.spendSummary;
    if (!s || !s.available) return [];
    const m = new Map();
    (s.months || []).forEach((mo) => (mo.accounts || []).forEach((a) => {
      const k = a.accountId || '(unknown account)';
      m.set(k, (m.get(k) || 0) + (a.amount || 0));
    }));
    return [...m.entries()]
      .map(([accountId, amount]) => ({ accountId, amount }))
      .sort((a, b) => b.amount - a.amount);
  }, [data]);

  const spend = data && data.spendSummary;
  const byService = data && data.spendByService;
  const anomalies = data && data.anomalies;
  const recs = data && data.recommendations;

  const unit = (spend && spend.available && spend.unit) || 'USD';
  const maxMonth = spend && spend.available
    ? Math.max(...(spend.months || []).map((m) => m.total), 0) : 0;
  const shownAccounts = accountTotals.slice(0, ACCOUNT_CAP);
  const droppedAccounts = accountTotals.slice(ACCOUNT_CAP);
  const droppedTotal = droppedAccounts.reduce((s, a) => s + a.amount, 0);
  const maxAccount = accountTotals.length ? accountTotals[0].amount : 0;

  const svcUnit = (byService && byService.available && byService.unit) || 'USD';
  const maxService = byService && byService.available
    ? Math.max(...(byService.services || []).map((s) => s.amount), 0) : 0;
  const hasOtherRow = !!(byService && byService.available
    && (byService.services || []).some((s) => s.service === 'Other'));

  const connected = !!(data && data.connected);

  // ---------------- Azure derived values ----------------
  // Safe to key by subscription id / service name alone: the whole payload
  // is single-scope by construction — the org pilot's own tenant, or
  // exactly this workspace's Lighthouse delegation (the backend filters the
  // enumeration to the connection's directory GUID and caches under this
  // workspace's id alone) — so there is no cross-tenant merge to guard here
  // either.
  const azureConnected = !!(azure && azure.connected);
  const azureSubs = (azureConnected && azure.subscriptions) || [];
  const azureUnit = (azureConnected && azure.unit) || 'USD';
  const azureSubTotals = azureSubs.map((s) => ({
    ...s,
    threeMonthTotal: s.available ? (s.months || []).reduce((sum, m) => sum + (m.total || 0), 0) : null,
  }));
  const maxAzureSub = Math.max(...azureSubTotals.map((s) => s.threeMonthTotal || 0), 0);
  const azureByService = azureConnected ? azure.byService : null;
  const maxAzureService = azureByService && azureByService.available
    ? Math.max(...(azureByService.services || []).map((s) => s.amount), 0) : 0;
  const azureHasOther = !!(azureByService && azureByService.available
    && (azureByService.services || []).some((s) => s.service === 'Other'));
  // Where the Azure block renders. The org pilot: always, for a master in
  // 'optimum' (as before). A client workspace: only when there is something
  // honest to say — the read connected, or an azure connection is on file
  // (the same /finops/connections listing showback already loads) and the
  // read did not connect, which earns the degraded strip instead of
  // silence. A workspace with no azure connection renders nothing Azure at
  // all — no noise for AWS-only clients.
  const showAzureSection = isOrgPilot
    ? iAmMaster
    : (azureConnected || (!!azureConn && ((azure && !azure.connected) || !!azureErr)));

  // ---------------- showback derived values + actions ----------------
  // Safe to key by accountId / factSheetId alone: this whole payload came
  // through ONE tenant's connection and ONE tenant's allocation rows — the
  // same single-tenant-by-construction reasoning as accountTotals above.
  const appNameById = useMemo(() => {
    const m = new Map();
    for (const a of apps) m.set(a.id, a.name);
    return m;
  }, [apps]);
  const allocByAccount = useMemo(() => {
    const m = new Map();
    for (const a of allocations) {
      if (!m.has(a.accountId)) m.set(a.accountId, []);
      m.get(a.accountId).push(a);
    }
    return m;
  }, [allocations]);
  // Every account the Allocate dialog should list: everything spendSummary
  // saw, plus any account that has rules but no spend in the window (its
  // rules must stay editable rather than disappearing with the spend).
  const dialogAccounts = useMemo(() => {
    const ids = new Set(accountTotals.map((a) => a.accountId));
    for (const id of allocByAccount.keys()) ids.add(id);
    return [...ids].sort();
  }, [accountTotals, allocByAccount]);

  const sbAvailable = !!(showback && showback.connected && showback.available);
  const sbMonths = (sbAvailable && showback.months) || [];
  const sbApps = (sbAvailable && showback.applications) || [];
  const sbUnalloc = (sbAvailable && showback.unallocated) || { total: 0, accounts: [] };
  const sbUnit = (sbAvailable && showback.unit) || 'USD';
  const sbOversubscribed = sbUnalloc.accounts.some((u) => u.unallocatedPct < 0);
  const showbackName = (a) => a.name || appNameById.get(a.factSheetId) || a.factSheetId;

  // Showback V2 — tag attribution. tagAttr is only present when the
  // connection has an application tag key; available:false carries the real
  // reason ('no-tagged-spend' gets its own honest strip below — that is the
  // never-activated-cost-allocation-tag case, AWS's real behavior).
  const tagAttr = (sbAvailable && showback.tagAttribution) || null;
  const tagActive = !!(tagAttr && tagAttr.available);
  const byTagTotal = tagActive ? sbApps.reduce((s, a) => s + (a.byTag || 0), 0) : 0;
  const byRuleTotal = tagActive ? sbApps.reduce((s, a) => s + (a.byAccountRule || 0), 0) : 0;
  const tagUnmatched = (tagActive && tagAttr.unmatched) || { total: 0, valueCount: 0, values: [] };

  const saveTagKey = async () => {
    if (!awsConn) return;
    const key = tagKeyDraft.trim();
    setTagBusy(true); setSbErr('');
    try {
      // The connections POST upserts on (tenant, provider); echoing the
      // current payer account back keeps it untouched, and the backend
      // preserves verification status when the account is unchanged.
      await api('/finops/connections', {
        method: 'POST',
        body: JSON.stringify({
          tenantId,
          provider: 'aws',
          payerAccountId: awsConn.payerAccountId || '',
          appTagKey: key || null,
        }),
      });
      await loadShowback();
    } catch (e) {
      setSbErr(e.message);
    } finally {
      setTagBusy(false);
    }
  };

  const saveAlloc = async (accountId) => {
    const d = draft[accountId] || {};
    const pct = Number(d.pct);
    const current = (allocByAccount.get(accountId) || [])
      .filter((a) => a.factSheetId !== d.factSheetId)
      .reduce((s, a) => s + (a.allocationPct || 0), 0);
    if (!d.factSheetId) { setAllocErr('Pick an application first.'); return; }
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      setAllocErr('The percentage must be greater than 0 and at most 100.');
      return;
    }
    // The same rule the server enforces (its refusal is the backstop) —
    // checked here too so the usual case fails before a round trip, with the
    // headroom named.
    if (current + pct > 100 + 1e-9) {
      setAllocErr(`That would allocate ${(current + pct).toFixed(1)}% of account ${accountId} — only ${(100 - current).toFixed(1)}% is unallocated.`);
      return;
    }
    setAllocBusy(true); setAllocErr('');
    try {
      await api('/finops/allocations', {
        method: 'POST',
        body: JSON.stringify({
          tenantId, accountId, factSheetId: d.factSheetId, allocationPct: pct,
          ...(d.note ? { note: d.note } : {}),
        }),
      });
      setDraft((prev) => ({ ...prev, [accountId]: {} }));
      await loadShowback();
    } catch (e) {
      setAllocErr(e.message);
    } finally {
      setAllocBusy(false);
    }
  };

  const removeAlloc = async (id) => {
    setAllocBusy(true); setAllocErr('');
    try {
      await api(`/finops/allocations?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
      await loadShowback();
    } catch (e) {
      setAllocErr(e.message);
    } finally {
      setAllocBusy(false);
    }
  };

  // The chargeback-statement seed: one row per application per month plus the
  // unallocated rows, per account — the same derived-or-labeled numbers on
  // screen, nothing recomputed for the file.
  const exportShowbackCsv = () => {
    if (!sbAvailable) return;
    const rows = [];
    for (const a of sbApps) {
      for (const m of a.monthly) {
        rows.push({ kind: 'application', name: showbackName(a), ref: a.factSheetId, month: m.start, amount: m.amount.toFixed(2) });
      }
    }
    for (const u of sbUnalloc.accounts) {
      for (const m of u.monthly) {
        rows.push({ kind: 'unallocated', name: `Unallocated — account ${u.accountId}`, ref: u.accountId, month: m.start, amount: m.amount.toFixed(2) });
      }
    }
    // Showback V2: unmatched tag values ride along so the file reconciles the
    // same way the screen does. (The payload lists the top values by spend —
    // the on-screen cap notice covers anything past them.)
    if (tagActive) {
      for (const v of tagUnmatched.values) {
        for (const m of v.monthly) {
          rows.push({ kind: 'unmatched-tag', name: `Unmatched tag value — ${v.value}`, ref: v.value, month: m.start, amount: m.amount.toFixed(2) });
        }
      }
    }
    exportCsv(`showback-${tenantId}.csv`, rows, [
      { label: 'Row type', value: 'kind' },
      { label: 'Application / bucket', value: 'name' },
      { label: 'Fact sheet id / account', value: 'ref' },
      { label: 'Month', value: 'month' },
      { label: 'Amount', value: 'amount' },
      { label: 'Currency', value: () => sbUnit },
    ]);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Dialog open={!!dismissTarget} onClose={() => !dismissBusy && setDismissTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Rule this out <HelpBubble topic="ruleOutRecommendation" /></DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>{(dismissTarget || {}).label}</Typography>
          <Typography variant="caption" color="text.secondary">
            This removes {fmtMoney((dismissTarget || {}).declinedMonthlySavings, 'USD')}/mo from the
            savings total, so the figure reflects what is actually achievable. It can be put back later.
          </Typography>
          <TextField
            autoFocus fullWidth multiline minRows={2} sx={{ mt: 2 }}
            label="Why can't this be done?" value={dismissReason}
            onChange={(e) => setDismissReason(e.target.value)}
            placeholder="e.g. vendor support matrix does not cover Graviton for this workload"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDismissTarget(null)} disabled={dismissBusy}>Cancel</Button>
          <Button variant="contained" onClick={confirmDismiss}
            disabled={dismissBusy || !dismissReason.trim()}>Rule it out</Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- header + connection strip ---------------- */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>FinOps</Typography>
        <HelpBubble topic="finops" />
        <IconButton onClick={() => { load(); loadAzure(); loadShowback(); }} title="Refresh" size="small" disabled={loading}>
          <RefreshIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }} />
        {!loading && data && (
          <Chip
            size="small"
            icon={connected ? <CloudDoneIcon fontSize="small" /> : <CloudOffIcon fontSize="small" />}
            label={connected ? 'Connected' : 'Not connected'}
            color={connected ? 'success' : 'default'}
          />
        )}
      </Stack>

      {/* Only where there is something to jump to: the master workspace has no
          showback (it is per client by definition), so offering the link there
          would scroll to nothing. */}
      {tenantId && tenantId !== 'optimum' && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Chip size="small" variant="outlined" clickable label="Spend summary"
            onClick={() => document.getElementById('finops-summary')?.scrollIntoView({ behavior: 'smooth' })} />
          <Chip size="small" variant="outlined" clickable label="Showback & attribution rules"
            onClick={() => document.getElementById('finops-showback')?.scrollIntoView({ behavior: 'smooth' })} />
        </Stack>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isOrgPilot
          ? 'Optimum’s own AWS organization (the pilot payer account) — read-only, via the SSM-published FinOps role.'
          : 'This workspace’s consolidated AWS spend, anomalies and cost-optimization recommendations — read-only, through the payer-account connection made under Cloud connections.'}
      </Typography>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}

      {loading && (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
      )}

      {/* ---------------- not connected: explain honestly ---------------- */}
      {!loading && data && !connected && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {data.reason === 'assume-failed' ? (
            <>
              An AWS connection is on file, but Primordia could not assume its read-only role just now
              — the stack may have been deleted from the payer account, or its parameters changed.
              {iAmAdmin && !isOrgPilot && (
                <> Open <b>Manage &rarr; Administration &rarr; Cloud connections</b> and press <b>Verify</b> to
                see the current error from AWS.</>
              )}
            </>
          ) : (
            <>
              {isOrgPilot
                ? 'The Optimum org pilot connection is not configured on this stage — the FinOps role parameter has not been published, so there is nothing to read yet.'
                : 'No AWS payer account is connected for this workspace yet, so there is no spend data to show.'}
              {iAmAdmin && !isOrgPilot && (
                <> An admin can connect one under <b>Manage &rarr; Administration &rarr; Cloud connections</b> —
                about two minutes in the client&rsquo;s own AWS console, read-only, no credentials typed anywhere.</>
              )}
              {!iAmAdmin && !isOrgPilot && (
                <> Ask your workspace administrator (or Optimum) to connect one under Cloud connections.</>
              )}
            </>
          )}
          {data.detail && (
            <Typography component="div" variant="caption" sx={{ mt: 1, fontFamily: 'monospace', opacity: 0.8 }}>
              {data.detail}
            </Typography>
          )}
        </Alert>
      )}

      {!loading && data && connected && (
        <Stack spacing={2}>
          {/* ---------------- spend summary ---------------- */}
          <Paper sx={{ p: 2 }}>
            <Typography id="finops-summary" variant="h6" sx={{ fontWeight: 700, color: HEAD, mb: 0.5, scrollMarginTop: 72 }}>Spend summary</Typography>
            {spend && spend.available === false && (
              <SectionUnavailable what="The monthly spend summary" reason={spend.reason} />
            )}
            {spend && spend.available && (
              <>
                <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: 30, fontWeight: 800, color: HEAD }}>
                    {fmtMoney(spend.orgTotal, unit)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    org total, {(spend.months || []).length} calendar month{(spend.months || []).length === 1 ? '' : 's'} (current month to date)
                  </Typography>
                </Stack>

                {/* per-month bars — plain Boxes, no chart library */}
                <Stack spacing={0.75} sx={{ mb: 2, maxWidth: 640 }}>
                  {(spend.months || []).map((m) => (
                    <Stack key={m.start} direction="row" alignItems="center" spacing={1.5}>
                      <Typography variant="body2" sx={{ width: 78, flexShrink: 0, color: 'text.secondary' }}>
                        {fmtMonth(m.start)}
                      </Typography>
                      <Bar value={m.total} max={maxMonth} />
                      <Typography variant="body2" sx={{ width: 110, flexShrink: 0, textAlign: 'right', fontWeight: 600 }}>
                        {fmtMoney(m.total, unit)}
                      </Typography>
                      <Box sx={{ width: 46, flexShrink: 0 }}>
                        {m.estimated && (
                          <Tooltip title="Cost Explorer marks this period estimated — the month is not final yet">
                            <Chip size="small" label="est." sx={{ height: 18, fontSize: 10, bgcolor: 'chipMuted.main', color: HEAD }} />
                          </Tooltip>
                        )}
                      </Box>
                    </Stack>
                  ))}
                </Stack>

                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: HEAD, mb: 0.5 }}>
                  Top accounts by spend
                </Typography>
                <Table size="small" sx={{ maxWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Account</TableCell>
                      <TableCell sx={{ width: '40%' }} />
                      <TableCell align="right">Spend ({(spend.months || []).length} mo)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shownAccounts.map((a) => (
                      <TableRow key={a.accountId}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{a.accountId}</TableCell>
                        <TableCell><Bar value={a.amount} max={maxAccount} /></TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMoney(a.amount, unit)}</TableCell>
                      </TableRow>
                    ))}
                    {!shownAccounts.length && (
                      <TableRow><TableCell colSpan={3}><Typography variant="body2" color="text.secondary">No linked-account spend in the period.</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
                {/* The cap says exactly what it dropped — a truncated list that
                    looks complete is how numbers get misquoted. */}
                {droppedAccounts.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    Showing the top {shownAccounts.length} of {accountTotals.length} accounts by spend —
                    the other {droppedAccounts.length} account{droppedAccounts.length === 1 ? '' : 's'} ({fmtMoney(droppedTotal, unit)} combined)
                    {' '}are included in every total above but not listed individually.
                  </Typography>
                )}
              </>
            )}
          </Paper>

          {/* ---------------- spend by service ---------------- */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: HEAD, mb: 0.5 }}>Spend by service</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Current calendar month to date.
              {hasOtherRow && ' The top 15 services are listed; every remaining service is rolled into the “Other” row, so the total is complete.'}
            </Typography>
            {byService && byService.available === false && (
              <SectionUnavailable what="The service breakdown" reason={byService.reason} />
            )}
            {byService && byService.available && (
              <Stack spacing={0.6} sx={{ maxWidth: 720 }}>
                {(byService.services || []).map((s) => (
                  <Stack key={s.service} direction="row" alignItems="center" spacing={1.5}>
                    <Typography variant="body2" noWrap sx={{ width: 260, flexShrink: 0 }} title={s.service}>
                      {s.service}
                    </Typography>
                    <Bar value={s.amount} max={maxService} />
                    <Typography variant="body2" sx={{ width: 110, flexShrink: 0, textAlign: 'right', fontWeight: 600 }}>
                      {fmtMoney(s.amount, svcUnit)}
                    </Typography>
                  </Stack>
                ))}
                {!(byService.services || []).length && (
                  <Typography variant="body2" color="text.secondary">No service-level spend recorded this month yet.</Typography>
                )}
                <Divider sx={{ my: 0.5 }} />
                <Stack direction="row" justifyContent="space-between" sx={{ maxWidth: 720 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Total</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtMoney(byService.orgTotal, svcUnit)}</Typography>
                </Stack>
              </Stack>
            )}
          </Paper>

          {/* ---------------- anomalies ---------------- */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: HEAD, mb: 0.5 }}>Cost anomalies</Typography>
            {anomalies && anomalies.available === false && (
              <SectionUnavailable what="Anomaly detection" reason={anomalies.reason} />
            )}
            {anomalies && anomalies.available && anomalies.count === 0 && (
              <>
                <Typography variant="body2" color="text.secondary">
                  No cost anomalies detected in the last {anomalies.days} days.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Cost Anomaly Detection only reports once a monitor exists in the payer account — an empty
                  list can also mean no monitor has been set up there yet.
                </Typography>
              </>
            )}
            {anomalies && anomalies.available && anomalies.count > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {anomalies.count} anomal{anomalies.count === 1 ? 'y' : 'ies'} in the last {anomalies.days} days, largest impact first.
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Impact</TableCell>
                      <TableCell align="right">Above expected</TableCell>
                      <TableCell>Root causes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(anomalies.anomalies || []).map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>{fmtDay(a.startDate)}{a.endDate ? ` – ${fmtDay(a.endDate)}` : ' – ongoing'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>
                          {fmtMoney(a.totalImpact, 'USD')}
                        </TableCell>
                        <TableCell align="right">{fmtPct(a.totalImpactPercentage)}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {(a.rootCauses || []).map((r, i) => (
                              <Chip
                                key={`${a.id}-${i}`}
                                size="small"
                                sx={{ bgcolor: 'chipMuted.main', color: HEAD, fontSize: 11 }}
                                label={[r.service, r.account, r.region, r.usageType].filter(Boolean).join(' · ') || 'unattributed'}
                              />
                            ))}
                            {!(a.rootCauses || []).length && (
                              <Typography variant="caption" color="text.secondary">none reported</Typography>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </Paper>

          {/* ---------------- recommendations: three independent cards ---------------- */}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: HEAD, mb: 1 }}>Savings recommendations</Typography>
            {recs && recs.available === false && (
              <SectionUnavailable what="Recommendations" reason={recs.reason} />
            )}
            {recs && recs.available !== false && (
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch">
                {/* ---- rightsizing (EC2, Cost Explorer) ---- */}
                <Paper sx={{ p: 2, flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD }}>EC2 rightsizing</Typography>
                  {recs.rightsizing && recs.rightsizing.available === false && (
                    <SectionUnavailable what="Rightsizing" reason={recs.rightsizing.reason} />
                  )}
                  {recs.rightsizing && recs.rightsizing.available && (
                    <>
                      <Typography sx={{ fontSize: 24, fontWeight: 800, color: HEAD }}>
                        {fmtMoney(recs.rightsizing.estimatedTotalMonthlySavings, recs.rightsizing.currencyCode || 'USD')}
                        <Typography component="span" variant="body2" color="text.secondary"> /month if all applied</Typography>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {recs.rightsizing.totalRecommendationCount} recommendation{recs.rightsizing.totalRecommendationCount === 1 ? '' : 's'} in total
                        {recs.rightsizing.totalRecommendationCount > (recs.rightsizing.top || []).length
                          && ` — the top ${(recs.rightsizing.top || []).length} by estimated savings are listed here; the rest are not shown`}
                        .
                      </Typography>
                      <Stack spacing={0.75}>
                        {(recs.rightsizing.top || []).map((r) => (
                          <Box key={r.resourceId || r.instanceName}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={r.resourceId || ''}>
                              {r.instanceName || r.resourceId || '(unnamed instance)'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {r.type === 'Terminate'
                                ? `terminate ${r.currentInstanceType || ''}`.trim()
                                : `${r.currentInstanceType || '?'} → ${r.targetInstanceType || '?'}`}
                              {' · '}{fmtMoney(r.estimatedMonthlySavings, r.currencyCode || 'USD')}/mo
                              {r.accountId ? ` · acct ${r.accountId}` : ''}
                            </Typography>
                          </Box>
                        ))}
                        {!(recs.rightsizing.top || []).length && (
                          <Typography variant="body2" color="text.secondary">No rightsizing opportunities found.</Typography>
                        )}
                      </Stack>
                    </>
                  )}
                </Paper>

                {/* ---- savings plans (Cost Explorer purchase recommendation) ---- */}
                <Paper sx={{ p: 2, flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD }}>Savings Plans</Typography>
                  {recs.savingsPlans && recs.savingsPlans.available === false && (
                    <SectionUnavailable what="The Savings Plans recommendation" reason={recs.savingsPlans.reason} />
                  )}
                  {recs.savingsPlans && recs.savingsPlans.available && (
                    <>
                      <Typography sx={{ fontSize: 24, fontWeight: 800, color: HEAD }}>
                        {fmtMoney(recs.savingsPlans.estimatedMonthlySavings, recs.savingsPlans.currencyCode || 'USD')}
                        <Typography component="span" variant="body2" color="text.secondary"> /month estimated</Typography>
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Compute Savings Plan, 1-year term, no upfront, 30-day lookback — the one scenario the
                        backend asks Cost Explorer to price.
                      </Typography>
                      <Stack spacing={0.4}>
                        <Typography variant="body2">
                          Savings vs on-demand: <b>{fmtPct(recs.savingsPlans.estimatedSavingsPercentage)}</b>
                        </Typography>
                        <Typography variant="body2">
                          Current on-demand spend (lookback): <b>{fmtMoney(recs.savingsPlans.currentOnDemandSpend, recs.savingsPlans.currencyCode || 'USD')}</b>
                        </Typography>
                        <Typography variant="body2">
                          Suggested hourly commitment: <b>{fmtMoney(recs.savingsPlans.estimatedCommitment, recs.savingsPlans.currencyCode || 'USD')}</b>
                        </Typography>
                      </Stack>
                    </>
                  )}
                </Paper>

                {/* ---- cost optimization hub ---- */}
                <Paper sx={{ p: 2, flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD }}>Cost Optimization Hub</Typography>
                  {recs.optimizationHub && recs.optimizationHub.available === false && (
                    <SectionUnavailable what="Cost Optimization Hub" reason={recs.optimizationHub.reason} />
                  )}
                  {recs.optimizationHub && recs.optimizationHub.available && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {recs.optimizationHub.total} recommendation{recs.optimizationHub.total === 1 ? '' : 's'} on the first page
                        {recs.optimizationHub.total > (recs.optimizationHub.top || []).length
                          && ` — the top ${(recs.optimizationHub.top || []).length} by estimated savings are listed here; the rest are not shown`}
                        .
                      </Typography>
                      <Stack spacing={0.75}>
                        {(recs.optimizationHub.top || []).map((r) => (
                          <Box key={r.id || r.resourceId}>
                            <Stack direction="row" alignItems="flex-start" spacing={1}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={r.resourceId || ''}>
                              {r.actionType || 'Recommendation'}: {r.currentResourceType || '?'}
                              {r.recommendedResourceType ? ` → ${r.recommendedResourceType}` : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {fmtMoney(r.estimatedMonthlySavings, r.currencyCode || 'USD')}/mo
                              {r.estimatedSavingsPercentage !== null && r.estimatedSavingsPercentage !== undefined
                                ? ` (${fmtPct(r.estimatedSavingsPercentage)})` : ''}
                              {r.accountId ? ` · acct ${r.accountId}` : ''}
                              {r.region ? ` · ${r.region}` : ''}
                            </Typography>
                              </Box>
                              {/* The refusal needs a reason, so this opens the
                                  dialog rather than toggling silently — a
                                  dismissal with no "why" is the thing the next
                                  consultant cannot act on. */}
                              <Button size="small" disabled={dismissBusy}
                                onClick={() => { setDismissTarget({
                                  provider: 'aws', source: 'optimization-hub',
                                  resourceKey: r.resourceId || r.id, actionType: r.actionType,
                                  declinedMonthlySavings: r.estimatedMonthlySavings,
                                  currency: r.currencyCode || 'USD',
                                  label: `${r.actionType || 'Recommendation'} — ${r.currentResourceType || ''}`,
                                }); setDismissReason(''); }}>
                                Can&rsquo;t do this
                              </Button>
                            </Stack>
                          </Box>
                        ))}
                        {!(recs.optimizationHub.top || []).length && (
                          <Typography variant="body2" color="text.secondary">No Hub recommendations right now.</Typography>
                        )}
                        {!!(recs.declinedMonthlySavings) && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            {fmtMoney(recs.declinedMonthlySavings, 'USD')}/mo has been ruled out as
                            not achievable ({recs.dismissedCount} item{recs.dismissedCount === 1 ? '' : 's'}),
                            and is already excluded from the totals above.
                          </Alert>
                        )}
                        {!!dismissals.length && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>Ruled out</Typography>
                            {dismissals.map((d) => (
                              <Stack key={d.id} direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                                <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }}>
                                  <b>{d.action_type}</b> · {d.reason}
                                  <span style={{ opacity: 0.7 }}> — {d.dismissed_by}</span>
                                </Typography>
                                <Button size="small" disabled={dismissBusy}
                                  onClick={() => restoreDismissal(d.id)}>Put back</Button>
                              </Stack>
                            ))}
                          </Box>
                        )}
                      </Stack>
                    </>
                  )}
                </Paper>
              </Stack>
            )}
          </Box>
        </Stack>
      )}

      {/* ================= Showback — client workspaces only ================
          Attribute this workspace's real AWS spend to its Applications via
          explicit allocation rules. Every dollar is either attributed by a
          rule or listed in the Unallocated bucket BY ACCOUNT — nothing is
          ever silently spread. Not rendered in the 'optimum' workspace: the
          backend refuses it (the pilot org has no client application estate
          to attribute to), and rendering the strip anyway would just repeat
          that refusal. */}
      {!isOrgPilot && (
        <Box sx={{ mt: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <Typography id="finops-showback" variant="h6" sx={{ fontWeight: 700, color: HEAD, scrollMarginTop: 72 }}>Showback</Typography>
            <Box sx={{ flex: 1 }} />
            {sbAvailable && (
              <Button size="small" startIcon={<DownloadIcon />} onClick={exportShowbackCsv}>
                Export CSV
              </Button>
            )}
            {iAmAdmin && sbAvailable && (
              <Button size="small" variant="outlined" onClick={() => { setAllocErr(''); setAllocOpen(true); }}>
                Allocate
              </Button>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This workspace&rsquo;s AWS spend attributed to its applications by explicit allocation
            rules — an account maps to one or more applications with percentage splits, and whatever
            no rule claims stays in the Unallocated bucket, named per account. The CSV is the seed of
            a chargeback statement: one row per application per month, plus the unallocated rows.
          </Typography>

          {sbErr && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSbErr('')}>{sbErr}</Alert>}
          {sbLoading && (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          )}

          {!sbLoading && showback && !showback.connected && (
            <Alert severity="info">
              Showback needs the workspace&rsquo;s AWS connection — the strip at the top of this page
              explains its current state.
            </Alert>
          )}
          {!sbLoading && showback && showback.connected && showback.available === false && (
            <SectionUnavailable what="Showback" reason={showback.reason} />
          )}

          {/* ---- Showback V2: the application tag key on the connection ----
              Admin-only, and independent of sbAvailable so a degraded
              showback still lets the key be fixed. Saving goes through the
              existing connections POST; the backend keeps the verification
              status when the payer account is unchanged. */}
          {!sbLoading && showback && showback.connected && iAmAdmin && awsConn && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD, mb: 0.5 }}>
                Application tag key
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                The cost-allocation tag key this estate uses to mean &ldquo;application&rdquo;
                (for example <b>App</b>, <b>Application</b> or <b>workload</b>). When set, spend tagged
                with it — the S3 bucket or RDS instance an application uses inside a <i>shared</i>
                account — is attributed to the Application fact sheet whose name matches the tag value;
                only the untagged remainder of each account goes through the account rules, so nothing
                is counted twice. Leave it empty to attribute by account rules alone.
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small" label="Tag key" sx={{ width: 260 }}
                  value={tagKeyDraft}
                  onChange={(e) => setTagKeyDraft(e.target.value)}
                  placeholder="e.g. App"
                  helperText={awsConn.appTagKey
                    ? `Current: ${awsConn.appTagKey}`
                    : 'Not set — account rules only'}
                />
                <Button
                  size="small" variant="contained"
                  disabled={tagBusy || tagKeyDraft.trim() === (awsConn.appTagKey || '')}
                  onClick={saveTagKey}
                >
                  {tagBusy ? 'Saving…' : 'Save'}
                </Button>
              </Stack>
            </Paper>
          )}

          {/* ---- Showback V2: the honest strips for a key that answered
                  nothing. 'no-tagged-spend' is AWS's real behavior for a tag
                  that exists on resources but was never ACTIVATED as a
                  cost-allocation tag — the wording tells the admin the actual
                  fix. Any other reason is a plain degraded strip. ---- */}
          {!sbLoading && sbAvailable && tagAttr && !tagAttr.available && (
            tagAttr.reason === 'no-tagged-spend' ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Tag key &lsquo;{tagAttr.tagKey}&rsquo; returned no tagged spend. All the spend came
                back in the untagged bucket, which has <b>two</b> possible causes and Cost Explorer
                reports them identically:
                <ol style={{ margin: '6px 0 0 18px' }}>
                  <li><b>No resource carries that tag.</b> Check the tag keys actually in use on the
                    account&rsquo;s resources — a key that is activated for billing but never applied
                    attributes nothing. If a different key is in use, change this connection&rsquo;s
                    tag key to match it rather than re-tagging the estate.</li>
                  <li><b>The key is not activated for cost allocation.</b> Activate it once in the
                    <b>payer</b> account&rsquo;s Billing console (Billing &rarr; Cost allocation tags);
                    it takes about 24 hours to appear in Cost Explorer. A key applied to resources
                    but left inactive here also attributes nothing.</li>
                </ol>
                Both must be true at once. Until then, spend is attributed by account rules only.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Tag attribution with key &lsquo;{tagAttr.tagKey}&rsquo; could not run this refresh:
                {' '}{tagAttr.reason || 'no reason reported'}. Spend below is attributed by account
                rules only.
              </Alert>
            )
          )}

          {!sbLoading && sbAvailable && (
            <Stack spacing={2}>
              {/* ---- Showback V2: the attribution summary line ---- */}
              {tagActive && (
                <Alert severity="info" icon={false}>
                  <b>{fmtMoney(byTagTotal, sbUnit)}</b> attributed by tag &lsquo;{tagAttr.tagKey}&rsquo;,
                  {' '}<b>{fmtMoney(byRuleTotal, sbUnit)}</b> by account rules,
                  {' '}<b>{fmtMoney(sbUnalloc.total, sbUnit)}</b> unallocated
                  {tagUnmatched.total > 0 && (
                    <> — and <b>{fmtMoney(tagUnmatched.total, sbUnit)}</b> of tagged spend matches no
                    application (see below)</>
                  )}
                  .
                </Alert>
              )}

              {/* ---- per-application spend ---- */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD, mb: 0.5 }}>
                  Spend by application
                </Typography>
                {!sbApps.length && (
                  <Typography variant="body2" color="text.secondary">
                    No allocation rules yet — every account&rsquo;s spend is in the Unallocated bucket
                    below.{iAmAdmin ? ' Press Allocate to attribute accounts to applications.'
                      : ' A workspace administrator (or Optimum) can add rules with the Allocate button.'}
                  </Typography>
                )}
                {sbApps.length > 0 && (
                  <Table size="small" sx={{ maxWidth: 1000 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Application</TableCell>
                        {sbMonths.map((m) => (
                          <TableCell key={m.start} align="right">
                            {fmtMonth(m.start)}{m.estimated ? ' (est.)' : ''}
                          </TableCell>
                        ))}
                        {tagActive && <TableCell align="right">By tag</TableCell>}
                        {tagActive && <TableCell align="right">By rules</TableCell>}
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sbApps.map((a) => (
                        <TableRow key={a.factSheetId}>
                          <TableCell sx={{ fontWeight: 600 }}>{showbackName(a)}</TableCell>
                          {a.monthly.map((m) => (
                            <TableCell key={m.start} align="right">{fmtMoney(m.amount, sbUnit)}</TableCell>
                          ))}
                          {tagActive && (
                            <TableCell align="right">{fmtMoney(a.byTag || 0, sbUnit)}</TableCell>
                          )}
                          {tagActive && (
                            <TableCell align="right">{fmtMoney(a.byAccountRule || 0, sbUnit)}</TableCell>
                          )}
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(a.total, sbUnit)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Paper>

              {/* ---- Showback V2: tagged spend that matched no application —
                      prominent, per value, with the real fix named. ---- */}
              {tagActive && tagUnmatched.total > 0 && (
                <Paper sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD }}>
                      Tagged, but matching no application
                    </Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: 'error.main' }}>
                      {fmtMoney(tagUnmatched.total, sbUnit)}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    These tag values carry real spend but name no Application fact sheet in this
                    workspace — rename the application to match the tag value, or retag the resources.
                    Until then this spend stays here, visibly, rather than being guessed onto an
                    application.
                  </Typography>
                  <Table size="small" sx={{ maxWidth: 900 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tag value</TableCell>
                        {sbMonths.map((m) => (
                          <TableCell key={m.start} align="right">
                            {fmtMonth(m.start)}{m.estimated ? ' (est.)' : ''}
                          </TableCell>
                        ))}
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tagUnmatched.values.map((v) => (
                        <TableRow key={v.value}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{v.value}</TableCell>
                          {v.monthly.map((m) => (
                            <TableCell key={m.start} align="right">{fmtMoney(m.amount, sbUnit)}</TableCell>
                          ))}
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(v.total, sbUnit)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {/* The cap says exactly what it dropped — same discipline as
                      the account table above. */}
                  {tagUnmatched.valueCount > tagUnmatched.values.length && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                      Showing the top {tagUnmatched.values.length} of {tagUnmatched.valueCount} unmatched
                      tag values by spend — the rest are included in the total above but not listed
                      individually.
                    </Typography>
                  )}
                </Paper>
              )}

              {/* ---- the Unallocated bucket, prominent and per account ---- */}
              <Paper sx={{ p: 2 }}>
                <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD }}>Unallocated</Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: sbUnalloc.total > 0 ? 'error.main' : HEAD }}>
                    {fmtMoney(sbUnalloc.total, sbUnit)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    of {fmtMoney(showback.orgTotal, sbUnit)} total spend has no allocation rule
                  </Typography>
                </Stack>
                {sbOversubscribed && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    An account below is allocated past 100% — its unallocated share is negative, which
                    means more than that account&rsquo;s whole spend is being attributed. Fix its rules
                    in the Allocate dialog.
                  </Alert>
                )}
                {!sbUnalloc.accounts.length && (
                  <Typography variant="body2" color="text.secondary">
                    Every account&rsquo;s spend is fully allocated — nothing is unattributed.
                  </Typography>
                )}
                {sbUnalloc.accounts.length > 0 && (
                  <Table size="small" sx={{ maxWidth: 900 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Account</TableCell>
                        <TableCell align="right">Unallocated share</TableCell>
                        {sbMonths.map((m) => (
                          <TableCell key={m.start} align="right">
                            {fmtMonth(m.start)}{m.estimated ? ' (est.)' : ''}
                          </TableCell>
                        ))}
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sbUnalloc.accounts.map((u) => (
                        <TableRow key={u.accountId}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{u.accountId}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: u.unallocatedPct < 0 ? 'error.main' : undefined }}>
                            {`${Number(u.unallocatedPct).toFixed(u.unallocatedPct % 1 ? 1 : 0)}%`}
                          </TableCell>
                          {u.monthly.map((m) => (
                            <TableCell key={m.start} align="right">{fmtMoney(m.amount, sbUnit)}</TableCell>
                          ))}
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(u.total, sbUnit)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            </Stack>
          )}

          {/* ---- Allocate dialog — admin only (the button never renders for
                  others; the server's canManage check is the backstop) ---- */}
          <Dialog open={allocOpen} onClose={() => !allocBusy && setAllocOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Allocate accounts to applications</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Every account seen in this workspace&rsquo;s spend is listed. An account&rsquo;s rules
                may not sum past 100% — the remainder stays in Unallocated, visibly, rather than being
                spread anywhere.
              </Typography>
              {allocErr && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAllocErr('')}>{allocErr}</Alert>}
              {!dialogAccounts.length && (
                <Typography variant="body2" color="text.secondary">
                  No accounts to allocate — no spend was returned for this workspace.
                </Typography>
              )}
              <Stack spacing={2}>
                {dialogAccounts.map((accountId) => {
                  const rules = allocByAccount.get(accountId) || [];
                  const allocated = rules.reduce((s, a) => s + (a.allocationPct || 0), 0);
                  const d = draft[accountId] || {};
                  const setD = (patch) => setDraft((prev) => ({ ...prev, [accountId]: { ...(prev[accountId] || {}), ...patch } }));
                  return (
                    <Paper key={accountId} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 1 }}>
                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{accountId}</Typography>
                        <Typography variant="caption" color={allocated > 100 ? 'error.main' : 'text.secondary'}>
                          {allocated.toFixed(allocated % 1 ? 1 : 0)}% allocated, {(100 - allocated).toFixed((100 - allocated) % 1 ? 1 : 0)}% free
                        </Typography>
                      </Stack>
                      {rules.map((r) => (
                        <Stack key={r.id} direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                          <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                            {r.factSheetName || appNameById.get(r.factSheetId) || r.factSheetId}
                            {!r.factSheetName && !appNameById.get(r.factSheetId) && (
                              <Typography component="span" variant="caption" color="text.secondary"> (fact sheet no longer exists)</Typography>
                            )}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, width: 56, textAlign: 'right' }}>
                            {r.allocationPct}%
                          </Typography>
                          <IconButton size="small" disabled={allocBusy} onClick={() => removeAlloc(r.id)} title="Remove this rule">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <TextField
                          select size="small" label="Application" sx={{ minWidth: 260, flex: 1 }}
                          value={d.factSheetId || ''}
                          onChange={(e) => setD({ factSheetId: e.target.value })}
                        >
                          {apps.map((a) => (
                            <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                          ))}
                          {!apps.length && <MenuItem value="" disabled>No Application fact sheets in this workspace</MenuItem>}
                        </TextField>
                        <TextField
                          size="small" label="%" type="number" sx={{ width: 90 }}
                          inputProps={{ min: 0.1, max: 100, step: 'any' }}
                          value={d.pct ?? ''}
                          onChange={(e) => setD({ pct: e.target.value })}
                        />
                        <TextField
                          size="small" label="Note (optional)" sx={{ width: 200 }}
                          value={d.note || ''}
                          onChange={(e) => setD({ note: e.target.value })}
                        />
                        <Button
                          size="small" variant="contained" disabled={allocBusy || !d.factSheetId || !d.pct}
                          onClick={() => saveAlloc(accountId)}
                        >
                          {rules.some((r) => r.factSheetId === d.factSheetId) ? 'Update' : 'Add'}
                        </Button>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAllocOpen(false)} disabled={allocBusy}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* ================= Azure ===========================================
          Two audiences, one block. In the 'optimum' workspace (master only):
          the org pilot, Optimum's OWN Azure tenant through the service
          principal — as it always was. In a client workspace: this
          workspace's OWN spend, read through its verified Lighthouse
          delegation (the boundary the Lighthouse stream left open is
          closed) — rendered only when the workspace actually has an azure
          connection, so an AWS-only client sees nothing Azure at all.
          Loads and fails independently of the AWS half above. */}
      {showAzureSection && (
        <Box sx={{ mt: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: HEAD }}>
              {isOrgPilot ? 'Azure (pilot)' : 'Azure'}
            </Typography>
            {!azureLoading && azure && (
              <Chip
                size="small"
                icon={azureConnected ? <CloudDoneIcon fontSize="small" /> : <CloudOffIcon fontSize="small" />}
                label={azureConnected ? 'Connected' : 'Not connected'}
                color={azureConnected ? 'success' : 'default'}
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isOrgPilot ? (
              <>Optimum&rsquo;s own Azure tenant — read-only, through a Cost Management Reader service
              principal. Every subscription the principal can see is listed; nothing here can write to Azure.</>
            ) : (
              <>This workspace&rsquo;s delegated Azure subscriptions — read-only, through the Azure
              Lighthouse delegation made under Cloud connections. Nothing here can write to Azure.</>
            )}
          </Typography>

          {azureErr && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAzureErr('')}>{azureErr}</Alert>}
          {azureLoading && (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          )}

          {/* not connected: a short honest strip naming the real state, never
              a blank and never a fake error */}
          {!azureLoading && azure && !azureConnected && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {azure.reason === 'auth-failed' ? (
                <>Azure credentials are on file, but signing in to Microsoft Entra failed — the service
                principal&rsquo;s client secret may have expired or been rotated.</>
              ) : azure.reason === 'no-delegation' ? (
                <>
                  An Azure connection is on file, but no delegated subscriptions are visible to Optimum
                  yet — the Lighthouse deployment may not have been made (or is still propagating, which
                  can take a few minutes), or the directory id on the connection is wrong.
                  {iAmAdmin && (
                    <> Press <b>Verify</b> under <b>Manage &rarr; Administration &rarr; Cloud
                    connections</b> to see the current state.</>
                  )}
                </>
              ) : azure.reason === 'not-configured' ? (
                isOrgPilot ? (
                  <>The Azure pilot is not configured on this stage — the service-principal parameters have
                  not been published, so there is nothing to read yet.</>
                ) : (
                  <>Azure could not be read — the configuration Optimum holds for reading Azure on this
                  stage is incomplete. This is Optimum&rsquo;s side to fix, not this workspace&rsquo;s.</>
                )
              ) : (
                <>Azure could not be read just now.</>
              )}
              {azure.detail && (
                <Typography component="div" variant="caption" sx={{ mt: 1, fontFamily: 'monospace', opacity: 0.8 }}>
                  {azure.detail}
                </Typography>
              )}
            </Alert>
          )}

          {!azureLoading && azureInsights && azureInsights.connected
            && azureInsights.advisor && azureInsights.advisor.available && (
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Azure Advisor &mdash; cost recommendations
                </Typography>
                <Chip size="small" label={`${azureInsights.advisor.total} across ${azureInsights.subscriptionsScanned} subscription${azureInsights.subscriptionsScanned === 1 ? '' : 's'}`} />
              </Stack>
              {/* Advisor states savings annually on some recommendation types
                  and monthly on others; the backend normalises both to monthly,
                  so this can label the number plainly. */}
              <Stack spacing={0.5}>
                {(azureInsights.advisor.top || []).map((r, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Stack key={r.id || i} direction="row" alignItems="flex-start" spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={r.resourceId || ''}>
                        {r.actionType || r.problem || 'Recommendation'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtMoney(r.estimatedMonthlySavings, r.currencyCode || azureUnit)}/mo
                        {r.impact ? ` \u00b7 ${r.impact} impact` : ''}
                        {r.impactedValue ? ` \u00b7 ${r.impactedValue}` : ''}
                      </Typography>
                    </Box>
                    <Button size="small" disabled={dismissBusy}
                      onClick={() => { setDismissTarget({
                        provider: 'azure', source: 'advisor',
                        resourceKey: r.resourceId || r.id, actionType: r.actionType || 'AdvisorCost',
                        declinedMonthlySavings: r.estimatedMonthlySavings,
                        currency: r.currencyCode || azureUnit,
                        label: r.actionType || r.problem || 'Advisor recommendation',
                      }); setDismissReason(''); }}>
                      Can&rsquo;t do this
                    </Button>
                  </Stack>
                ))}
              </Stack>
              {azureInsights.advisor.partial && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Some subscriptions refused to answer, so this list is a floor rather than a total.
                </Alert>
              )}
            </Box>
          )}

          {!azureLoading && azureConnected && (
            <Stack spacing={2}>
              {/* ---------------- per-subscription spend ---------------- */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD, mb: 0.5 }}>
                  Spend by subscription
                </Typography>
                <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: 30, fontWeight: 800, color: HEAD }}>
                    {fmtMoney(azure.orgTotal, azureUnit)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    across {azureSubs.length} subscription{azureSubs.length === 1 ? '' : 's'}, 3 calendar months (current month to date)
                  </Typography>
                </Stack>
                <Table size="small" sx={{ maxWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Subscription</TableCell>
                      <TableCell align="right">Month to date</TableCell>
                      <TableCell sx={{ width: '30%' }} />
                      <TableCell align="right">3-month total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {azureSubTotals.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {s.id}
                          </Typography>
                        </TableCell>
                        {s.available ? (
                          <>
                            <TableCell align="right">{fmtMoney(s.monthToDate, azureUnit)}</TableCell>
                            <TableCell><Bar value={s.threeMonthTotal} max={maxAzureSub} /></TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMoney(s.threeMonthTotal, azureUnit)}</TableCell>
                          </>
                        ) : (
                          <TableCell colSpan={3}>
                            <Chip
                              size="small"
                              label={s.reason === 'rate-limited'
                                ? 'rate-limited — not counted in totals'
                                : s.reason === 'time-budget'
                                  ? 'ran out of time this refresh — retry shortly'
                                  : s.reason || 'unavailable'}
                              sx={{ bgcolor: 'chipMuted.main', color: HEAD, fontSize: 11 }}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {!azureSubTotals.length && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary">
                            The service principal sees no subscriptions.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                {azureSubTotals.some((s) => !s.available) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    A subscription marked rate-limited was refused by Azure Cost Management&rsquo;s throttle;
                    one marked &ldquo;ran out of time&rdquo; was skipped so this page could answer before the
                    request timed out. Either way its spend is missing from every total above — refresh later
                    to fill it in.
                  </Typography>
                )}
              </Paper>

              {/* ---------------- service breakdown across subscriptions ---------------- */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD, mb: 0.5 }}>
                  Spend by service
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Current calendar month to date, summed across subscriptions.
                  {azureHasOther && ' The top 15 services are listed; every remaining service is rolled into the “Other” row, so the total is complete.'}
                </Typography>
                {azureByService && azureByService.available === false && (
                  <SectionUnavailable what="The service breakdown" reason={azureByService.reason} cloud="Azure" />
                )}
                {azureByService && azureByService.available && (
                  <>
                    {azureByService.subscriptionsSkipped > 0 && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        This breakdown covers {azureByService.subscriptionsCounted} of
                        {' '}{azureByService.subscriptionsCounted + azureByService.subscriptionsSkipped} subscriptions —
                        the rest could not be queried this refresh (rate limits, errors, or the time budget)
                        and are missing from these numbers.
                      </Alert>
                    )}
                    <Stack spacing={0.6} sx={{ maxWidth: 720 }}>
                      {(azureByService.services || []).map((s) => (
                        <Stack key={s.service} direction="row" alignItems="center" spacing={1.5}>
                          <Typography variant="body2" noWrap sx={{ width: 260, flexShrink: 0 }} title={s.service}>
                            {s.service}
                          </Typography>
                          <Bar value={s.amount} max={maxAzureService} />
                          <Typography variant="body2" sx={{ width: 110, flexShrink: 0, textAlign: 'right', fontWeight: 600 }}>
                            {fmtMoney(s.amount, azureUnit)}
                          </Typography>
                        </Stack>
                      ))}
                      {!(azureByService.services || []).length && (
                        <Typography variant="body2" color="text.secondary">No service-level spend recorded this month yet.</Typography>
                      )}
                      <Divider sx={{ my: 0.5 }} />
                      <Stack direction="row" justifyContent="space-between" sx={{ maxWidth: 720 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>Total</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtMoney(azureByService.orgTotal, azureUnit)}</Typography>
                      </Stack>
                    </Stack>
                  </>
                )}
              </Paper>
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
}
